import { LoadStatus } from '@servicetitan/cp-ui';
import { FormValidators, InputFieldState } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AuthApi, InviteRequest } from 'api';
import { FormState } from 'formstate';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../common/stores/special-settings.store';
import { ContactInfoMode, validateContactInfo } from '../utils/contact-info.validator';
import { AllowedContactMethods, contactMethodToMode } from './contact-method';

const SIGNUP_CONTACT_METHODS_KEY = 'signup-contact-methods';

interface SignupContactMethodsSettings {
    allowedContactMethods?: number;
}

const errorMessageForMode = (mode: ContactInfoMode): string => {
    if (mode === 'email-only') {
        return 'Enter a valid email address';
    }
    if (mode === 'phone-only') {
        return 'Enter a valid phone number';
    }
    return 'Enter a valid email or phone number';
};

@injectable()
export class AuthRegisterStore {
    @observable status = LoadStatus.None;
    @observable contactMode: ContactInfoMode = 'email-only';

    @computed get canSubmit() {
        return !!this.form.$.contact.value && !this.form.hasError;
    }

    form = new FormState({
        contact: new InputFieldState('')
            .validators(
                FormValidators.requiredWithCustomMessage('Enter a value'),
                (value: string) => {
                    const result = validateContactInfo(value, this.contactMode);
                    if (result.isValid) {
                        return false;
                    }
                    if (result.kind === 'phone' && result.country === 'unsupported') {
                        return 'Phone numbers from this country are not supported';
                    }
                    return errorMessageForMode(this.contactMode);
                }
            )
            .disableAutoValidation(),
    });

    constructor(
        @inject(AuthApi) private authApi: AuthApi,
        @inject(SpecialSettingsStore) private specialSettings: SpecialSettingsStore
    ) {
        makeObservable(this);
        this.loadContactMode();
    }

    submit = async () => {
        await this.form.validate();

        if (this.form.hasError) {
            return;
        }

        runInAction(() => (this.status = LoadStatus.Loading));

        try {
            await this.authApi.client.selfInvite(
                new InviteRequest({
                    contactValue: this.form.$.contact.value.trim(),
                })
            );

            runInAction(() => (this.status = LoadStatus.Ok));
        } catch {
            runInAction(() => (this.status = LoadStatus.Error));
        }
    };

    private async loadContactMode() {
        const raw = await this.specialSettings.getAsync<SignupContactMethodsSettings>(
            SIGNUP_CONTACT_METHODS_KEY
        );

        if (this.specialSettings.failed) {
            // fail-closed: keep 'email-only'
            return;
        }

        const settings = raw ?? {};
        const value = settings.allowedContactMethods;

        if (
            value === AllowedContactMethods.Email ||
            value === AllowedContactMethods.Phone ||
            value === AllowedContactMethods.Both
        ) {
            runInAction(() => {
                this.contactMode = contactMethodToMode[value];
            });
        }
        // out-of-range or undefined: keep 'email-only'
    }
}
